import { stripHtmlTags, truncateText } from "@msys/common";
import {
  DataGrid,
  getFormattedPrice,
  GridColDef,
  LoadingSpinner,
  StateStore,
} from "@msys/ui";
import { Box, Grid, Stack, TablePagination } from "@mui/material";
import { useTolgee, useTranslate } from "@tolgee/react";
import React from "react";
import { To, useNavigate } from "react-router-dom";
import {
  PimSearchProductsSortBy,
  PimSearchProductsSorting,
} from "../../../../clients/graphqlTypes.js";
import { useCardsNumber } from "../../../commons/hooks/useCardsNumber.js";
import { CollectionView } from "../../../commons/hooks/useCollectionView.js";
import type { PaginationModel } from "../../../commons/pagination/usePaginationParams.js";
import { RESULTS_PER_PAGE_OPTIONS } from "../../../constants.js";
import { getProductBrand, getProductFullDescription } from "../helper.js";
import {
  PreferenceRankingMarker,
  preferenceRankings,
} from "./PreferenceRankingMarker.js";
import {
  ProductCardItem,
  ProductListItem,
  type ProductsListViewProduct,
} from "./ProductItem.js";
import { SupplierProductStatusChip } from "../../../main-routes/products/SupplierProductStatusChip.js";

const FIELD_TO_COLUMN: Record<string, PimSearchProductsSortBy> = {
  title: "title",
  brand: "brand",
  articleNumber: "articleNumber",
  manufacturerArticleNumber: "brandArticleNumber",
};

const COLUMN_TO_FIELD: Record<PimSearchProductsSortBy, string> = {
  title: "title",
  brand: "brand",
  brandLine: "brandLine",
  articleNumber: "articleNumber",
  brandArticleNumber: "manufacturerArticleNumber",
};

interface Props<P extends ProductsListViewProduct> {
  items: P[];
  totalCount: number;
  activeView: CollectionView;
  loading: boolean;
  CardActionButton: React.ComponentType<{ product: P }>;
  TableActions: React.ComponentType<{ product: P }>;
  TableCheckbox?: React.ComponentType<{ product: P }>;
  onClick?: (product: P) => void;
  /**
   * Marks one row as the product currently assigned to the consumer's entity.
   * Card and list layouts only — in the table layout `rowSelectionModel`
   * already means checkbox multi-select, a different concept.
   */
  isSelected?: (product: P) => boolean;
  getProductTo?: (product: P) => To;
  paginationModel: PaginationModel;
  setPaginationModel: (newPaginationModel: PaginationModel) => void;
  pageSizeOptions?: number[];
  // Sorting is optional: views with a fixed order (e.g. product lists, ordered
  // by rank) omit it, which also disables the sortable column headers.
  sortModel?: PimSearchProductsSorting[];
  setSortModel?: (model: PimSearchProductsSorting[]) => void;
  stateStore: StateStore;
  tableActionsWidth: number;
  // Custom row id (e.g. product lists key rows by the list-item id since the
  // same product may appear more than once); defaults to the row's `id`.
  getRowId?: (product: P) => string;
  // Escape hatch to pass extra props to the underlying table `DataGrid`
  // (e.g. `rowReordering` + `onRowOrderChange`).
  dataGridProps?: Partial<React.ComponentProps<typeof DataGrid<P>>>;
}

export function ProductsListView<P extends ProductsListViewProduct>({
  items,
  totalCount,
  activeView,
  loading,
  CardActionButton,
  TableActions,
  TableCheckbox,
  onClick,
  isSelected,
  getProductTo,
  paginationModel,
  setPaginationModel,
  pageSizeOptions = RESULTS_PER_PAGE_OPTIONS,
  sortModel,
  setSortModel,
  stateStore,
  tableActionsWidth,
  getRowId,
  dataGridProps,
}: Props<P>) {
  const language = useTolgee(["language"]).getLanguage()!;
  const { t } = useTranslate(["Product", "Global"]);
  const navigate = useNavigate();
  const { spacing, sizeMeasureRef, columns } = useCardsNumber();

  const tableColumns = React.useMemo<GridColDef<P>[]>(
    () => [
      ...(TableCheckbox
        ? ([
            {
              field: "checkbox",
              headerName: "",
              width: 50,
              sortable: false,
              resizable: false,
              renderCell: ({ row: product }) => (
                <TableCheckbox product={product} />
              ),
            },
          ] as GridColDef<P>[])
        : []),
      {
        field: "title",
        headerName: t("Title", {
          ns: "Product",
        }),
        flex: 2,
        minWidth: 100,
        sortable: Boolean(setSortModel),
        renderCell: ({ row: product }) => {
          const rankings = preferenceRankings(product);
          return rankings.length > 0 ? (
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ alignItems: "center", minWidth: 0 }}
            >
              <PreferenceRankingMarker rankings={rankings} />
              <span>{product.texts?.title}</span>
            </Stack>
          ) : (
            product.texts?.title
          );
        },
      },
      {
        field: "description",
        headerName: t("Description", {
          ns: "Product",
        }),
        flex: 3,
        minWidth: 150,
        sortable: false,
        renderCell: ({ row: product }) =>
          truncateText(stripHtmlTags(getProductFullDescription(product)), 140),
      },
      {
        field: "supplier",
        headerName: t("Supplier", {
          ns: "Product",
        }),
        flex: 2,
        minWidth: 100,
        sortable: false,
        renderCell: ({ row: product }) => product.supplier?.title,
      },
      {
        field: "brand",
        headerName: t("Brand", {
          ns: "Product",
        }),
        flex: 2,
        minWidth: 100,
        sortable: Boolean(setSortModel),
        renderCell: ({ row: product }) => getProductBrand(product) || null,
      },
      {
        field: "articleNumber",
        headerName: t("Article Number", {
          ns: "Product",
        }),
        flex: 1,
        minWidth: 50,
        sortable: Boolean(setSortModel),
        renderCell: ({ row: product }) => product.articleNumber,
      },
      {
        field: "manufacturerArticleNumber",
        headerName: t("Manufacturer article number", {
          ns: "Product",
        }),
        flex: 1,
        minWidth: 50,
        sortable: Boolean(setSortModel),
        renderCell: ({ row: product }) => product.manufacturerArticleNumber,
      },
      {
        field: "netPrice",
        headerName: t("Net Price", {
          ns: "Product",
        }),
        flex: 1,
        minWidth: 50,
        sortable: false,
        renderCell: ({ row: product }) =>
          product.pricing?.netPrice
            ? getFormattedPrice(product.pricing?.netPrice, language)
            : "–",
      },
      {
        field: "listPrice",
        headerName: t("List Price", {
          ns: "Product",
        }),
        flex: 1,
        minWidth: 50,
        sortable: false,
        renderCell: ({ row: product }) =>
          product.pricing?.listPrice
            ? getFormattedPrice(product.pricing?.listPrice, language)
            : "–",
      },
      {
        field: "status",
        headerName: t("SupplierProductStatus", {
          ns: "Product",
        }),
        flex: 1,
        minWidth: 40,
        sortable: false,
        renderCell: ({ row: product }) =>
          product.status && product.status.value !== "available" ? (
            <SupplierProductStatusChip status={product.status.value} />
          ) : null,
      },
      {
        field: "actions",
        headerName: "",
        width: tableActionsWidth,
        sortable: false,
        resizable: false,
        align: "right",
        renderCell: ({ row: product }) => (
          <Stack
            direction="row"
            spacing={0.5}
            onClick={e => {
              e.stopPropagation();
            }}
            sx={{
              alignItems: "center",
            }}
          >
            <TableActions product={product} />
          </Stack>
        ),
      },
    ],
    [t, language, tableActionsWidth, TableCheckbox, TableActions, setSortModel]
  );

  switch (activeView) {
    case "gallery": {
      return (
        <Stack
          ref={sizeMeasureRef}
          direction="column"
          spacing={1}
          sx={{
            flex: 1,
            overflow: "auto",
            minHeight: 0,
          }}
        >
          {loading ? (
            <LoadingSpinner />
          ) : !items.length ? (
            <Box
              sx={{
                padding: 2,
                display: "flex",
                justifyContent: "center",
              }}
            >
              {t("There seems to be nothing here", {
                ns: "Global",
              })}
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  overflow: "auto",
                  flex: 1,
                  minHeight: 0,
                }}
              >
                <Grid container spacing={spacing}>
                  {items.map((product, index) => (
                    <Grid key={product.id} size={columns}>
                      <ProductCardItem
                        key={product.id}
                        product={product}
                        getProductTo={getProductTo}
                        onClick={onClick}
                        ActionButton={CardActionButton}
                        selected={isSelected?.(product)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
              <TablePagination
                component="div"
                rowsPerPageOptions={pageSizeOptions}
                count={totalCount}
                page={paginationModel.page}
                onPageChange={(event, newPage) => {
                  setPaginationModel({
                    pageSize: paginationModel.pageSize,
                    page: newPage,
                  });
                }}
                rowsPerPage={paginationModel.pageSize}
                onRowsPerPageChange={event => {
                  setPaginationModel({
                    pageSize: parseInt(event.target.value, 10),
                    page: 0,
                  });
                }}
                labelRowsPerPage={t("Per page:", {
                  ns: "Global",
                })}
              />
            </>
          )}
        </Stack>
      );
    }
    case "list":
      if (loading) {
        return <LoadingSpinner />;
      }
      if (!items.length) {
        return (
          <Box
            sx={{
              padding: 2,
              display: "flex",
              justifyContent: "center",
            }}
          >
            {t("There seems to be nothing here", {
              ns: "Global",
            })}
          </Box>
        );
      }
      return (
        <Stack
          direction="column"
          spacing={1}
          sx={{
            flex: 1,
            overflow: "auto",
            minHeight: 0,
          }}
        >
          <Stack
            direction="column"
            spacing={1}
            sx={{
              overflow: "auto",
              flex: 1,
              minHeight: 0,
            }}
          >
            {items.map((product, index) => (
              <ProductListItem
                key={product.id}
                product={product}
                getProductTo={getProductTo}
                onClick={onClick}
                ActionButton={CardActionButton}
                selected={isSelected?.(product)}
              />
            ))}
          </Stack>
          <TablePagination
            component="div"
            rowsPerPageOptions={pageSizeOptions}
            count={totalCount}
            page={paginationModel.page}
            onPageChange={(event, newPage) => {
              setPaginationModel({
                pageSize: paginationModel.pageSize,
                page: newPage,
              });
            }}
            rowsPerPage={paginationModel.pageSize}
            onRowsPerPageChange={event => {
              setPaginationModel({
                pageSize: parseInt(event.target.value, 10),
                page: 0,
              });
            }}
            labelRowsPerPage={t("Per page:", {
              ns: "Global",
            })}
          />
        </Stack>
      );

    case "table":
      return (
        <DataGrid
          stateStore={stateStore}
          density="compact"
          onCellClick={(params, event) => {
            event.defaultMuiPrevented = true;
            if (
              [
                "title",
                "description",
                "supplier",
                "brand",
                "articleNumber",
              ].includes(params.field)
            ) {
              onClick?.(params.row);
              if (getProductTo) {
                navigate(getProductTo(params.row));
              }
            }
          }}
          loading={loading}
          hideFooter={totalCount === 0}
          sortingMode={setSortModel ? "server" : "client"}
          sortModel={
            sortModel
              ? sortModel
                  .filter(s => COLUMN_TO_FIELD[s.column] in FIELD_TO_COLUMN)
                  .map(s => ({
                    field: COLUMN_TO_FIELD[s.column],
                    sort: s.direction,
                  }))
              : undefined
          }
          onSortModelChange={
            setSortModel
              ? newModel => {
                  setSortModel(
                    newModel
                      .filter(({ field }) => field in FIELD_TO_COLUMN)
                      .map(({ field, sort }) => ({
                        column: FIELD_TO_COLUMN[field],
                        direction: sort ?? "asc",
                      }))
                  );
                  if (paginationModel.page !== 0) {
                    setPaginationModel({
                      pageSize: paginationModel.pageSize,
                      page: 0,
                    });
                  }
                }
              : undefined
          }
          columns={tableColumns}
          rows={items}
          getRowId={getRowId}
          paginationModel={paginationModel}
          onPaginationModelChange={newPaginationModel => {
            setPaginationModel(newPaginationModel);
          }}
          pageSizeOptions={pageSizeOptions}
          rowCount={totalCount}
          {...dataGridProps}
        />
      );
  }
  return null;
}
